import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Button } from "@/components/ui/button";
import { useAdminSession } from "@/auth/AdminSession";
import { clearTenantSelection } from "@/auth/tenantSelection";
import ApiService from "@/services/ApiService";
import { useData, roles } from "@/pages/management/ui";
import {
  ShieldCheck,
  LayoutDashboard,
  Briefcase,
  FileText,
  Users,
  Bell,
  User,
  LogOut,
  Building2,
  Bookmark,
  Share2,
  Activity,
  Menu,
} from "lucide-react";
import { useState } from "react";
export default function IndexLayout() {
  const { session, tenant, can } = useAdminSession();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const notifications = useData<{ unread: number }>("notifications");
  const links = [
    ...(session.is_platform_admin
      ? [{ name: "プラットフォーム", link: "/platform", icon: ShieldCheck }]
      : []),
    ...(tenant
      ? [
          { name: "ダッシュボード", link: "/", icon: LayoutDashboard },
          { name: "求人一覧", link: "/jobs", icon: Briefcase },
          ...(can("applications.read")
            ? [{ name: "応募一覧", link: "/job-applications", icon: FileText }]
            : []),
          ...(tenant.type === "agent"
            ? [
                { name: "共有求人", link: "/shared-jobs", icon: Share2 },
                { name: "保存した求人", link: "/saved-jobs", icon: Bookmark },
                ...(can("applications.read")
                  ? [
                      {
                        name: "紹介した応募",
                        link: "/applied-list",
                        icon: FileText,
                      },
                    ]
                  : []),
                ...(can("candidates.manage")
                  ? [{ name: "候補者・履歴書", link: "/cv-list", icon: Users }]
                  : []),
              ]
            : []),
          { name: "組織情報", link: "/organization", icon: Building2 },
          ...(can("staff.manage")
            ? [
                {
                  name: "スタッフ管理",
                  link: "/organization/staff",
                  icon: Users,
                },
              ]
            : []),
          ...(can("audit.read") || can("audit.self")
            ? [
                {
                  name: "監査ログ",
                  link: "/organization/audit",
                  icon: Activity,
                },
              ]
            : []),
        ]
      : []),
    { name: "通知", link: "/notifications", icon: Bell },
    { name: "アカウント", link: "/account", icon: User },
  ];
  const logout = async () => {
    try {
      await ApiService.post("auth/logout", {});
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearTenantSelection();
      navigate("/login");
    }
  };
  return (
    <div className="min-h-screen lg:flex">
      <aside
        className={
          (menu ? "block" : "hidden") +
          " lg:block lg:w-64 lg:shrink-0 bg-slate-900 text-white p-4 lg:sticky lg:top-0 lg:h-screen overflow-y-auto"
        }
      >
        <Link
          to={session.is_platform_admin ? "/platform" : "/"}
          className="flex items-center gap-3 text-xl font-bold px-3 py-4"
        >
          <img src="/logo.svg" alt="" className="w-8 h-8" />
          OOAK JOB
        </Link>
        {tenant &&
          can("jobs.write") &&
          (tenant.type === "agent" || session.company_posting) && (
            <Button asChild className="w-full my-4">
              <Link to="/jobs/create">求人を登録</Link>
            </Button>
          )}
        <nav aria-label="メインメニュー" className="space-y-1">
          {links.map(({ name, link, icon: Icon }) => (
            <NavLink
              end={link === "/" || link === "/platform"}
              onClick={() => setMenu(false)}
              key={link}
              to={link}
              className={({ isActive }) =>
                "flex items-center gap-3 px-3 py-3 rounded-lg text-sm " +
                (isActive ? "bg-blue-600" : "hover:bg-slate-800")
              }
            >
              <Icon className="w-5 h-5" />
              {name}
            </NavLink>
          ))}
        </nav>
        <button
          className="flex items-center gap-3 px-3 py-4 text-sm"
          onClick={logout}
        >
          <LogOut className="w-5 h-5" />
          ログアウト
        </button>
      </aside>
      <div className="min-w-0 flex-grow">
        <header className="sticky top-0 z-40 bg-background/95 border-b flex flex-wrap gap-3 justify-between items-center px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              aria-label="メニュー"
              className="lg:hidden"
              onClick={() => setMenu(!menu)}
            >
              <Menu />
            </Button>
            <div>
              <p className="font-semibold">
                {tenant?.name ?? "プラットフォーム管理"}
              </p>
              <p className="text-xs text-muted-foreground">
                {roles[session.platform_role ?? tenant?.role ?? ""]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/notifications")}
            >
              <Bell className="w-4 h-4 mr-2" />
              {notifications.data?.unread
                ? notifications.data.unread + "件"
                : "通知"}
            </Button>
            <ThemeToggle />
            <Link to="/account" className="text-sm">
              {session.user.name}
            </Link>
          </div>
        </header>
        {(session.is_platform_admin || session.memberships.length > 1) && (
          <div className="border-b bg-blue-50 text-blue-950 dark:bg-slate-800 dark:text-blue-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>
              {tenant
                ? "管理対象：" +
                  tenant.name +
                  "（" +
                  (tenant.type === "agent" ? "紹介会社" : "企業") +
                  "）"
                : "全体を表示中"}
            </span>
            <div className="flex gap-2">
              {session.platform_role === "platform_admin" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    sessionStorage.removeItem("job-admin-reason");
                    window.dispatchEvent(new Event("job-reason-changed"));
                  }}
                >
                  操作理由を次回入力
                </Button>
              )}
              {tenant && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    clearTenantSelection();
                    window.location.assign(
                      session.is_platform_admin
                        ? "/platform"
                        : "/organizations",
                    );
                  }}
                >
                  組織を切り替える
                </Button>
              )}
            </div>
          </div>
        )}
        <main className="min-h-screen bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
